import React, { useMemo, useState } from "react";
import { FileService } from "src/utils/file.service";


import Thumbnail from "src/components/thumbnail";

import { Assessment, Lesson, Section } from "src/types/coaching/courses";
import Text from "src/components/text";

import _ from "lodash";
import cx from "classnames";
import styles from "./cards.module.scss";
import { useSpring, animated } from "@react-spring/web";
import { LearningRecordCourse } from "src/types/coaching/learners";
import Collapse from "src/pages/asset/components/animation/collapse";
import pluralize from "pluralize";
import CircularProgress from "../../../progress";

export interface LessonCardProps {
  course: LearningRecordCourse;
  lesson: Lesson;
  index: number;
  lessonIndex: number | string;
  className?: string;
  active: boolean;
  disabled?: boolean;
  children?: React.ReactNode;

  // eslint-disable-next-line no-unused-vars
  onClick: (index: number) => void;
}

export const LessonCard: React.FC<LessonCardProps> = ({ course, lesson, index, lessonIndex, className, active, disabled, children, onClick }) => {
  const content = _.get(lesson, 'content');
  const name = _.get(lesson, 'name') || "Untitled Lesson";

  const fileType = useMemo(() => {
    return FileService.getFileType(_.get(content, "metadata", ""));
  }, [content]);

  const readTime = useMemo(() => {
    const videoDuration = _.get(content, "metadata.video_duration", 0);
    const readDuration = _.get(content, "metadata.read_time", 0);

    const duration = videoDuration || readDuration;
    if (duration === 0) {
      return null;
    }

    return FileService.formatDuration(videoDuration || readDuration);
  }, [content]);

  const percentCompleted = useMemo(() => {
    const sessions = course.sessions || [];
    const item = sessions?.find((item) => item.id === lesson.id && item.type === 'lesson');

    return item?.percentCompleted || 0;
  }, [course]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e) {
      e.stopPropagation();
    }
    onClick(index);
  }

  return (
    <div className={cx(styles.entity, styles.lesson, className, {
      [styles.active]: active,
      [styles.disabled]: disabled,
    })} onClick={handleClick}>
      <div className={styles.index}>{lessonIndex} /</div>
      <div className={styles.entityDetail}>
        <Thumbnail className={styles.icon} media={_.get(content, "icon")} defaultImage="/images/assets/default_asset.jpg" />
        <div className={styles.details}>
          <Text className={styles.name} text={name} lines={1} />
          <div className={styles.metadata}>
            <span className={styles.value}>{fileType}</span>
            {readTime &&
              <>
                <span className={styles.separator}>&nbsp;</span>
                <span className={styles.value}>{readTime}</span>
              </>
            }
          </div>
        </div>
      </div>

      <div className={styles.right}>
        {!disabled &&
          <>
            {percentCompleted == 0 && (
              <div className={cx(styles.progress, styles.notStarted)}>
                <i className="paperflite-circle-dashed"></i>
              </div>
            )}
            {percentCompleted > 0 && percentCompleted < 100 && (
              <div className={cx(styles.progress, styles.inProgress)}>
                <CircularProgress size={200} progress={percentCompleted} />
              </div>
            )}
          </>
        }

        {percentCompleted === 100 && (
          <div className={cx(styles.progress, styles.completed)}>
            <i className="paperflite-tick"></i>
          </div>
        )}
      </div>
    </div>
  );
}

export interface AssessmentCardProps {
  course: LearningRecordCourse;
  assessment: Assessment;
  assessmentIndex: number | string;
  index: number;
  className?: string;
  active: boolean;
  disabled?: boolean;
  children?: React.ReactNode;

  // eslint-disable-next-line no-unused-vars
  onClick: (index: number) => void;
}

export const AssessmentCard: React.FC<AssessmentCardProps> = ({ course, assessment, index, className, active, disabled, children, onClick, assessmentIndex }) => {

  const { percentCompleted, completed } = useMemo(() => {
    const sessions = course.sessions || [];
    const item = sessions?.find((item) => item.id === assessment.id && item.type === 'assessment');

    return {
      percentCompleted: (item?.percentCompleted || 0) * 100,
      completed: item?.status === 'COMPLETED',
    }
  }, [course]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e) {
      e.stopPropagation();
    }
    onClick(index);
  }

  return (
    <div className={cx(styles.entity, styles.assessment, className, {
      [styles.active]: active,
      [styles.disabled]: disabled,
    })} onClick={handleClick}>
      <div className={styles.index}>{assessmentIndex} /</div>
      <div className={styles.entityDetail}>
        <Thumbnail className={styles.icon} media={null} defaultImage="/images/coaching/assessment.svg" />
        <div className={styles.details}>
          <Text className={styles.name} text={_.get(assessment, 'name') || `Untitled Assessment`} lines={1} />
          <div className={styles.metadata}>
            <span className={styles.value}>Assessment</span>
            <span className={styles.separator}>&nbsp;</span>
            <span className={styles.value}>{_.size(_.get(assessment, 'questions'))} Questions</span>
          </div>
        </div>
      </div>

      <div className={styles.right}>
        {completed && (
          <div className={cx(styles.progress, styles.completed)}>
            <i className="paperflite-tick"></i>
          </div>
        )}
      </div>
    </div>
  );
}

export interface SectionCardProps {
  course: LearningRecordCourse;
  section: Section;
  index: number;
  sectionIndex: number;
  className?: string;
  active?: boolean;
  disabled?: boolean;
  contentChildIndex?: number;
  children?: React.ReactNode;

  dragIndicator?: {
    index: number;
    parent: string | null;
    before: boolean;
  };

  // eslint-disable-next-line no-unused-vars
  onClick: (index: number, section: number) => void;

  // eslint-disable-next-line no-unused-vars
  onRemove?: (parent: string | null, id: string) => void;

  // eslint-disable-next-line no-unused-vars
  onAddLessonClicked?: (parentUuid?: string | null) => void;

  // eslint-disable-next-line no-unused-vars
  onAddAssessmentClicked?: (parentUuid?: string | null) => void;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  course,
  section,
  index, // Index of section in course
  sectionIndex, // Display index of section in course
  contentChildIndex, // Index of content in section
  className,
  disabled,
  dragIndicator,
  children,
  onClick,
}) => {

  const [collapsed, setCollapsed] = useState(false);

  const { lessons, assessments } = useMemo(() => {
    const lessonEntityTypes = ["lesson", "podcast", "simulation"];

    const lessons = _.size(
      _.filter(section.contents, (item) =>
        lessonEntityTypes.includes(item.entityType),
      ),
    );
    
    const assessments = _.size(_.filter(section.contents, { entityType: "assessment" }));

    return { lessons, assessments };
  }, [section]);

  const { completed, total } = useMemo(() => {
    const sessions = course.sessions || [];
    const total = _.size(section.contents);

    const completedItems = _.filter(section.contents, (contentItem) => {
      const session = sessions?.find((item) => item.id === contentItem.id);

      if (contentItem.entityType === 'lesson') {
        return session != null;
      } else if (contentItem.entityType === 'assessment') {
        return session?.status === 'COMPLETED';
      }else if (contentItem.entityType === 'podcast') {
        return session?.status === 'COMPLETED';
      }
    });

    return {
      completed: _.size(completedItems),
      total: total,
    }
  }, [course, section]);

  const handleClick = () => {
    setCollapsed(!collapsed);
  };

  const handleContentClick = (index: number, contentIndex: number) => {
    if (onClick) {
      onClick(index, contentIndex);
    }
  }

  const getLessonIndexForContent = (sectionIndex) => {
    return `${index + 1}.${sectionIndex + 1}`;
  };


  return (
    <div
      className={cx(styles.sectionEntity, className, {
        [styles.readonly]: disabled,
      })}
      onClick={handleClick}
    >
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.sectionIndex}>{sectionIndex} /</div>
          <div className={styles.sectionDetail}>
            <div className={styles.nameWrapper}>
              <Text className={styles.name} text={section.name || "Untitled Section"} lines={1} />
             
            </div>
            <div className={styles.metadata}>
              <span className={styles.value}>{lessons} {pluralize("Lesson", lessons)}</span>
              <span className={styles.separator}>&nbsp;</span>
              <span className={styles.value}>{assessments} {pluralize("Assessment", assessments)}</span>
            </div>
          </div>

          <div className={styles.rightSection}>
            {total > 0 &&
              <div className={styles.progress}>{completed}/{total}</div>
            }
            <div className={styles.divider} />
            <i className={cx("paperflite-up-arrow", styles.expandIcon, {
                [styles.collapsed]: collapsed,
              })}></i>
          </div>
        </div>
      </div>

      {section.description && (
        <p className={styles.description}>{section.description}</p>
      )}

      <Collapse isOpen={!collapsed}>
        <div className={styles.body}>
          {section.contents.map((content, contentIdx) => {
            return (
              <React.Fragment key={content.uuid}>
                {dragIndicator?.index === contentIdx && dragIndicator?.before &&
                  <DropIndicator key={`drop-indicator-${dragIndicator?.index}-before`} />
                }

                {content.entityType === "lesson" && (
                  <LessonCard
                    key={content.uuid}
                    course={course}
                    lesson={content as Lesson}
                    lessonIndex={getLessonIndexForContent(contentIdx)}
                    index={contentIdx}
                    disabled={disabled}
                    active={contentIdx === contentChildIndex}
                    onClick={() => handleContentClick(index, contentIdx)}
                  />
                )}

                {content.entityType === "assessment" && (
                  <AssessmentCard
                    key={content.uuid}
                    index={contentIdx}
                    course={course}
                    assessmentIndex={getLessonIndexForContent(contentIdx)}
                    assessment={content as Assessment}
                    disabled={disabled}
                    active={contentIdx === contentChildIndex}
                    onClick={() => handleContentClick(index, contentIdx)}
                  />
                )}

                {content.entityType === "podcast" && (
                  <PodcastCard
                    key={content.uuid}
                    index={contentIdx}
                    course={course}
                    podcastIndex={getLessonIndexForContent(contentIdx)}
                    podcast={content}
                    disabled={disabled}
                    active={contentIdx === contentChildIndex}
                    onClick={() => handleContentClick(index, contentIdx)}
                  />
                )}

                {content.entityType === "simulation" && (
                  <SimulationCard
                    key={content.uuid}
                    index={contentIdx}
                    course={course}
                    simulationIndex={getLessonIndexForContent(contentIdx)}
                    simulation={content}
                    disabled={disabled}
                    active={contentIdx === contentChildIndex}
                    onClick={() => handleContentClick(index, contentIdx)}
                  />
                )}

                {dragIndicator?.index === contentIdx && !dragIndicator?.before &&
                  <DropIndicator key={`drop-indicator-${dragIndicator?.index}-after`} />
                }
              </React.Fragment>
            );
          })}
        </div>

      </Collapse>
    </div>
  );
};

export interface PodcastCardProps {
  course: LearningRecordCourse;
  podcast: any;
  index: number;
  podcastIndex: number | string;
  className?: string;
  active: boolean;
  disabled?: boolean;

  onClick: (index: number) => void;
}

export const PodcastCard: React.FC<PodcastCardProps> = ({
  course,
  podcast,
  index,
  podcastIndex,
  className,
  active,
  disabled,
  onClick,
}) => {
  const content = _.get(podcast, "content");
  const name = _.get(podcast, "name") || "Untitled Podcast";

  const fileType = useMemo(() => _.get(content,"entityType","FILE"),[content]);

  const audioDuration = useMemo(() => {
    const duration = _.get(content, "audio.duration", 0);
    if (!duration) {
      return null;
    }
    return FileService.formatDuration(duration);
  }, [content]);

  const percentCompleted = useMemo(() => {
    const sessions = course.sessions || [];
    const item = sessions.find(
      (item) => item.id === podcast.id && item.type === "podcast"
    );

    return item?.percentCompleted || 0;
  }, [course, podcast.id]);

  const completed = useMemo(() => {
    const sessions = course.sessions || [];
    const item = sessions.find(
      (item) => item.id === podcast.id && item.type === "podcast"
    );

    return item?.status === 'COMPLETED';
  }, [course, podcast.id]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onClick(index);
  };

  return (
    <div
      className={cx(styles.entity, styles.podcast, className, {
        [styles.active]: active,
        [styles.disabled]: disabled,
      })}
      onClick={handleClick}
    >
      <div className={styles.index}>{podcastIndex} /</div>

      <div className={styles.entityDetail}>
        <Thumbnail
          className={styles.icon}
          media={_.get(content, "banner.background")}
           defaultImage="/images/assets/podcastThumbnail.jpg"
        />

        <div className={styles.details}>
          <Text className={styles.name} text={name} lines={1} />

          <div className={styles.metadata}>
            <span className={styles.value}>{fileType.toUpperCase()}</span>

            {audioDuration && (
              <>
                <span className={styles.separator}>&nbsp;</span>
                <span className={styles.value}>{audioDuration}</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.right}>
        {!disabled && !completed && (
          <>
            {percentCompleted === 0 && (
              <div className={cx(styles.progress, styles.notStarted)}>
                <i className="paperflite-circle-dashed"></i>
              </div>
            )}

            {percentCompleted > 0 && percentCompleted < 100 && (
              <div className={cx(styles.progress, styles.inProgress)}>
                <CircularProgress size={200} progress={percentCompleted} />
              </div>
            )}
          </>
        )}

        {completed && (
          <div className={cx(styles.progress, styles.completed)}>
            <i className="paperflite-tick"></i>
          </div>
        )}
      </div>
    </div>
  );
};

export interface SimulationCardProps {
  course: LearningRecordCourse;
  simulation: any;
  index: number;
  simulationIndex: number | string;
  className?: string;
  active: boolean;
  disabled?: boolean;

  onClick: (index: number) => void;
}


export const SimulationCard: React.FC<SimulationCardProps> = ({
  course,
  simulation,
  index,
  simulationIndex,
  className,
  active,
  disabled,
  onClick,
}) => {
  const content = _.get(simulation, "content");
  const name = _.get(simulation, "name") || "Untitled Simulation";

  const duration = useMemo(() => {
    const finalDuration = _.get(simulation,"content.duration",null);
    return finalDuration;
  }, [simulation]);

  const fileType = useMemo(() => _.get(content,"entityType","FILE"),[content]);

  const percentCompleted = useMemo(() => {
    const sessions = course.sessions || [];
    const item = sessions.find(
      (item) => item.id === simulation.id && item.type === "simulation"
    );

    return item?.percentCompleted || 0;
  }, [course, simulation.id]);

  const callType = useMemo(() => _.get(simulation, "content.type", ""), [simulation]);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onClick(index);
  };

  return (
    <div
      className={cx(styles.entity, styles.simulation, className, {
        [styles.active]: active,
        [styles.disabled]: disabled,
      })}
      onClick={handleClick}
    >
      <div className={styles.index}>{simulationIndex} /</div>

      <div className={styles.entityDetail}>
      <Thumbnail
          className={cx(styles.icon,{
          [styles.discovery]: callType === "discovery",
          [styles.coldcall]: callType === "cold_call",
        })}
          media={_.get(content, "banner.background")}
          defaultImage="/images/assets/simulationThumbnail.jpg"
        />

        <div className={styles.details}>
          <Text className={styles.name} text={name} lines={1} />

          <div className={styles.metadata}>
            <span className={styles.value}>{fileType.toUpperCase()}</span>

            {duration !== 0 && (
              <>
                <span className={styles.separator}>&nbsp;</span>
                <span className={styles.value}>{duration} Minutes</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className={styles.right}>
        {percentCompleted === 100 && (
          <div className={cx(styles.progress, styles.completed)}>
            <i className="paperflite-tick"></i>
          </div>
        )}
      </div>
    </div>
  );
};

interface ContentEmptyCardProps {
  index: number;
}

export const ContentEmptyCard: React.FC<ContentEmptyCardProps> = ({ index }) => {
  return (
    <div className={cx(styles.emptyCard, {
      [styles.first]: index === 0,
    })}>
      <div className={styles.icon}></div>
      <div className={styles.text}>
        <div className={styles.line} style={{ width: '8.5rem' }}></div>
        <div className={styles.line} style={{ width: '5.5rem' }}></div>
      </div>
    </div>
  );
}

export const ContentPlaceholderCard: React.FC = () => {
  return (
    <div className={cx(styles.emptyCard)}>
      <div className={styles.icon}></div>
      <div className={styles.text}>
        <div className={styles.line} style={{ width: '8.5rem' }}></div>
        <div className={styles.line} style={{ width: '5.5rem' }}></div>
      </div>
    </div>
  );
}

export const DropIndicator: React.FC = () => {
  const height = useSpring({
    from: { height: 0 },
    to: { height: 80 },
    config: { tension: 300, friction: 20 }
  });

  return (
    <animated.div style={height} className={styles.dropIndicator} />
  )
}